// src/pages/dashboard/customer/offers/CustomerOffers.jsx
import { useState, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import api from '../../../../lib/api';
import { error, success } from '../../../../components/common/Toast';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import NegotiationModal from '../../../../components/offers/NegotiationModal';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  MessageCircle, 
  ArrowLeftRight, 
  Trash2,
  ChevronLeft,
  ChevronRight 
} from 'lucide-react';
import { formatMoney, formatDate } from '../../../../lib/helpers';
import { useConfirm } from '../../../../hooks/useConfirm';
import RatingDisplay from '../../../../components/common/RatingDisplay';

export default function CustomerOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [negoOpen, setNegoOpen] = useState(false);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const { confirm } = useConfirm();

  useEffect(() => {
    fetchOffers();
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();

    return () => emblaApi.off('select', onSelect);
  }, [emblaApi]);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();
  const scrollTo = (index) => emblaApi?.scrollTo(index);

  const fetchOffers = async () => {
    try {
      const res = await api.get('/offers/my-offers');
      const sentOffers = res.data.data?.sent || res.data.data?.sent || [];
      setOffers(sentOffers);
    } catch {
      error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  const openNegotiation = (offer) => {
    setSelectedOffer(offer);
    setNegoOpen(true);
  };

  const cancelOffer = async (offerId) => {
    const confirmed = await confirm({
      title: "Cancel Offer?",
      message: "This offer will be permanently removed and cannot be recovered.",
      confirmText: "Yes, Cancel Offer",
      cancelText: "Keep Offer",
      danger: true,
    });

    if (!confirmed) return;

    setOffers(prev => prev.map(o => o.id === offerId ? { ...o, loading: true } : o));

    try {
      await api.delete(`/offers/${offerId}`);
      success('Offer cancelled successfully');
      setOffers(prev => prev.filter(o => o.id !== offerId));
    } catch {
      error('Failed to cancel offer');
      setOffers(prev => prev.map(o => o.id === offerId ? { ...o, loading: false } : o));
    }
  };

  if (loading) return <LoadingSpinner size="lg" text="Loading your offers..." />;

  if (offers.length === 0) {
    return (
      <div className="text-center py-20 px-6">
        <p className="text-2xl sm:text-3xl text-gray-500">No offers sent yet</p>
        <p className="mt-4 text-lg sm:text-xl text-gray-600">Browse nannies and make your first offer!</p>
      </div>
    );
  }

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-center text-4xl sm:text-5xl font-bold mb-12 sm:mb-16">My Sent Offers</h1>

        <div className="relative">
          {/* Carousel Viewport */}
          <div className="overflow-hidden rounded-3xl" ref={emblaRef}>
            <div className="flex">
              {offers.map((offer) => {
                const nanny = offer.made_to;
                const hasNegotiations = offer.negotiations?.length > 0;
                const latestNegotiation = offer.negotiations?.[offer.negotiations.length - 1];

                return (
                  <div key={offer.id} className="flex-none w-full min-w-0 px-4 sm:px-6">
                    <div className={`bg-white rounded-3xl shadow-2xl overflow-hidden border-l-8 sm:border-l-12 transition-all
                      ${offer.status === 'pending' ? 'border-yellow-500' :
                        offer.status === 'accepted' ? 'border-green-500' :
                        'border-red-500'}`}
                    >
                      <div className="p-8 sm:p-10 lg:p-14">
                        {/* Header: Nanny + Amount */}
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8 mb-8 sm:mb-10">
                          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-8 w-full">
                            <div className="w-28 h-28 sm:w-32 sm:h-32 bg-gradient-to-br from-primary to-secondary rounded-3xl flex items-center justify-center text-white text-5xl sm:text-6xl font-bold shadow-2xl flex-shrink-0">
                              { nanny.profile_picture && !nanny.profile_picture.includes('default-avatar') ? 
                                <img
                                  src={process.env.REACT_APP_IMAGE_URL+nanny?.profile_picture}
                                  alt={`${nanny.full_name}`}
                                  className="w-full h-full rounded-3xl object-cover"
                                /> : 
                                nanny?.full_name?.[0]?.toUpperCase() || 'U'
                              }
                            </div>
                            <div className="text-center sm:text-left flex-1">
                              <h3 className="text-3xl sm:text-4xl font-bold">{nanny?.full_name}</h3>
                              <div className="my-4"><RatingDisplay rating={nanny?.rating} /></div>                        
                              <p className="text-lg sm:text-xl text-gray-500 mt-2">Offer #{offer.id}</p>
                            </div>
                          </div>

                          <div className="text-center lg:text-right w-full lg:w-auto">
                            <p className="text-5xl sm:text-6xl font-black text-primary">
                              {formatMoney(offer.amount)}
                            </p>
                            <p className="text-xl sm:text-2xl text-gray-600">per month</p>
                          </div>
                        </div>

                        {/* Conditions */}
                        <div className="bg-gray-50 p-6 sm:p-8 rounded-2xl mb-8 sm:mb-10">
                          <p className="text-lg sm:text-xl text-gray-800 whitespace-pre-line leading-relaxed">
                            {offer.conditions || 'No additional conditions'}
                          </p>
                        </div>

                        {/* Status Badge */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8 mb-8 sm:mb-10">
                          <span className={`inline-flex items-center gap-3 sm:gap-4 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-xl sm:text-2xl shadow-lg
                            ${offer.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              offer.status === 'accepted' ? 'bg-green-100 text-green-800' :
                              'bg-red-100 text-red-800'}`}
                          >
                            {offer.status === 'pending' && <Clock size={28} sm={32} />}
                            {offer.status === 'accepted' && <CheckCircle size={28} sm={32} />}
                            {offer.status === 'rejected' && <XCircle size={28} sm={32} />}
                            {offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}
                          </span>

                          <div className="text-gray-600 text-base sm:text-lg text-center sm:text-left">
                            Sent: {formatDate(offer.created_at)} • Expires: {formatDate(offer.expires_at)}
                          </div>
                        </div>

                        {/* Negotiation Alert */}
                        {offer.status === 'pending' && hasNegotiations && (
                          <div className="p-6 sm:p-8 bg-orange-50 rounded-3xl border-2 border-orange-300 mb-8 sm:mb-10">
                            <p className="text-xl sm:text-2xl font-bold text-orange-800 flex items-center gap-4 justify-center sm:justify-start">
                              <ArrowLeftRight size={32} sm={36} /> Negotiation in Progress
                            </p>
                            <p className="text-lg sm:text-xl text-orange-700 mt-4 text-center sm:text-left">
                              Latest counter:{' '}
                              <span className="font-black">{formatMoney(latestNegotiation.proposed_amount)}</span>
                              {latestNegotiation.proposed_terms && (
                                <span className="block mt-2 italic text-base sm:text-lg">"{latestNegotiation.proposed_terms}"</span>
                              )}
                            </p>
                          </div>
                        )}

                        {/* Action Buttons */}
                        {offer.status === 'pending' && (
                          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-end mt-10 sm:mt-12">
                            <button
                              onClick={() => openNegotiation(offer)}
                              className="px-8 sm:px-10 py-5 sm:py-6 bg-orange-600 hover:bg-orange-700 text-white font-bold text-lg sm:text-xl rounded-3xl shadow-xl transition flex items-center justify-center gap-3 sm:gap-4"
                            >
                              <MessageCircle size={28} sm={32} />
                              {hasNegotiations ? 'Continue Negotiation' : 'Negotiate'}
                            </button>

                            <button
                              onClick={() => cancelOffer(offer.id)}
                              disabled={offer.loading}
                              className="px-8 sm:px-10 py-5 sm:py-6 bg-red-600 hover:bg-red-700 disabled:opacity-70 text-white font-bold text-lg sm:text-xl rounded-3xl shadow-xl transition flex items-center justify-center gap-3 sm:gap-4"
                            >
                              <Trash2 size={28} sm={32} />
                              Cancel Offer
                            </button>
                          </div>
                        )}

                        {offer.status === 'accepted' && offer.contract_created && (
                          <div className="text-center py-10 sm:py-12">
                            <p className="text-3xl sm:text-4xl font-bold text-green-600">
                              Contract Created Successfully!
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Negotiation History */}
                      {hasNegotiations && (
                        <div className="bg-gray-50 px-8 sm:px-14 py-8 sm:py-10 border-t-4 border-gray-200">
                          <h4 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 flex items-center gap-4">
                            <ArrowLeftRight className="text-primary" size={32} sm={36} />
                            Negotiation History
                          </h4>
                          <div className="space-y-6 sm:space-y-8">
                            {offer.negotiations.map((nego) => (
                              <div key={nego.id} className="flex flex-col sm:flex-row justify-between items-center sm:items-start py-6 border-b last:border-0 gap-4">
                                <div className="text-center sm:text-left">
                                  <p className="text-2xl sm:text-3xl font-bold text-primary">{formatMoney(nego.proposed_amount)}</p>
                                  {nego.proposed_terms && (
                                    <p className="text-base sm:text-lg text-gray-600 italic mt-3">"{nego.proposed_terms}"</p>
                                  )}
                                </div>
                                <p className="text-gray-500 text-sm sm:text-lg">{formatDate(nego.created_at, true)}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={scrollPrev}
            disabled={selectedIndex === 0}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 -translate-x-12 sm:-translate-x-16 bg-white/95 hover:bg-white rounded-full p-4 sm:p-5 shadow-2xl transition disabled:opacity-30"
          >
            <ChevronLeft size={40} sm={48} />
          </button>

          <button
            onClick={scrollNext}
            disabled={selectedIndex === offers.length - 1}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 translate-x-12 sm:translate-x-16 bg-white/95 hover:bg-white rounded-full p-4 sm:p-5 shadow-2xl transition disabled:opacity-30"
          >
            <ChevronRight size={40} sm={48} />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-3 sm:gap-4 mt-12 sm:mt-16">
          {offers.map((_, idx) => (
            <button
              key={idx}
              onClick={() => scrollTo(idx)}
              className={`h-4 rounded-full transition-all duration-300 ${
                idx === selectedIndex
                  ? 'bg-primary w-12 sm:w-16'
                  : 'bg-gray-300 w-4 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>

        <NegotiationModal
          offer={selectedOffer}
          isOpen={negoOpen}
          onClose={() => {
            setNegoOpen(false);
            setSelectedOffer(null);
          }}
          onSuccess={fetchOffers}
        />
      </div>
    </div>
  );
}